import SFCard from "../card";
import { SFStack } from "../grid";
import SFTypography from "../typography";
import SFButton from "../button";
import getStyled from "../../hooks/getStyled";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";

const { styled } = getStyled();

const StyledCard = styled(SFCard, {
  shouldForwardProp: (prop) => prop !== "padding",
})(({ theme, padding }) => ({
  display: "flex",
  flexDirection: "column",
  height: "100%",
  width: "100%",
  "& .header": {
    width: "100%",
    paddingTop: padding ? theme.spacing(2.5) : null,
    backgroundColor: theme.palette.grey100.main,
    position: "relative",
    borderBottom: `1px solid ${theme.palette.grey225.main}`,
    "& .chips-container": {
      position: "absolute",
      right: 5,
      top: 5,
      width: "100%",
      display: "flex",
      justifyContent: "end",
      zIndex: 2,
    },
    "& .logo-container": {
      height: "100%",
      display: "flex",
      alignItems: "flex-end",
      justifyContent: "center",
      "& .MuiSvgIcon-root": {
        width: "unset",
        height: "155px",
      },
    },
  },
}));

const StyledCardTitle = styled("div")(({ theme }) => ({
  textOverflow: "ellipsis",
  overflow: "hidden",
  display: "-webkit-box",
  webkitLineClamp: "2",
  webkitBoxOrient: "vertical",
}));

const StyledButtonTop = styled("div")(({ theme }) => ({
  height: "auto",
  [theme.breakpoints.up("md")]: {
    display: "none",
  },
}));

const StyledButtonBottom = styled("div")(({ theme }) => ({
  height: "auto",
  [theme.breakpoints.down("md")]: {
    display: "none",
  },
}));

const StyledFooter = styled(SFStack)(({ theme }) => ({
  padding: "10px 20px",
  borderTop: `1px solid ${theme.palette.grey100.main}`,
}));

const Footer = ({ t, footer }) => (
  <StyledFooter direction="row" justifyContent="flex-start" spacing={2.5}>
    <SFStack direction="row" justifyContent="flex-start" alignItems="center">
      {footer.owner.avatar}
      <SFTypography
        variant="body2"
        color="grey400"
        sx={{ ml: 1 }}
        component="div"
      >
        {t("components.launchCard.modifiedByLabel")}
        <SFTypography variant="body2" component="div">
          {footer.owner.name}
        </SFTypography>
      </SFTypography>
    </SFStack>
    <SFTypography variant="body2" color="grey400" component="div">
      {t("components.launchCard.dateLabel")}
      <SFTypography variant="body2" component="div">
        {footer.date}
      </SFTypography>
    </SFTypography>
  </StyledFooter>
);

const SFThumbnail = ({
  logo,
  title,
  subtitle,
  body,
  buttonLabel,
  onClick,
  numbers,
  chips,
  disabled,
  padding = false,
  paddingLogo = false,
  footer = null,
  buttonIcon = null,
}) => {
  const { t } = useTranslation();

  return (
    <StyledCard padding={padding}>
      <div className="header">
        {chips && <div className="chips-container">{chips}</div>}
        <div
          className="logo-container"
          style={{
            ...(paddingLogo && { padding: "20px 0" }),
          }}
        >
          {logo}
        </div>
      </div>
      <SFStack
        justifyContent="space-between"
        // padding={2}
        style={{ height: "100%", padding: "20px" }}
      >
        <SFStack
          spacing={2.5}
          style={{
            paddingBottom: body || numbers ? "20px" : 0,
            height: "100%",
          }}
        >
          <SFStack spacing={2.5}>
            {title && (
              <StyledCardTitle>
                <SFTypography variant="cardTitle">{title}</SFTypography>
              </StyledCardTitle>
            )}
            {subtitle}
          </SFStack>
          {onClick && (
            <StyledButtonTop>
              <SFButton
                variant={"secondary"}
                onClick={onClick}
                fullWidth
                endIcon={buttonIcon}
              >
                {buttonLabel}
              </SFButton>
            </StyledButtonTop>
          )}
          {body && <SFTypography variant="body1">{body}</SFTypography>}
        </SFStack>
        <SFStack
          spacing={2.5}
          style={{
            paddingTop: !body && !numbers ? "20px" : 0,
            paddingBottom: body || numbers ? "20px" : 0,
          }}
        >
          {numbers && (
            <SFStack direction="row" justifyContent="flex-start" spacing={3.75}>
              {numbers.map((n, index) => (
                <SFStack direction="row" alignItems="flex-end" key={index}>
                  <SFTypography
                    variant="cardNumber"
                    sx={{ marginRight: "4px" }}
                  >
                    {n.count}
                  </SFTypography>
                  <SFTypography variant="cardNumberText">
                    {n.label}
                  </SFTypography>
                </SFStack>
              ))}
            </SFStack>
          )}
        </SFStack>
        {onClick && (
          <StyledButtonBottom>
            <SFButton
              variant={"secondary"}
              onClick={onClick}
              fullWidth
              disabled={disabled}
              endIcon={buttonIcon}
            >
              {buttonLabel}
            </SFButton>
          </StyledButtonBottom>
        )}
      </SFStack>
      {footer && <Footer t={t} footer={footer} />}
    </StyledCard>
  );
};

SFThumbnail.propTypes = {
  logo: PropTypes.element.isRequired,
  title: PropTypes.string,
  subtitle: PropTypes.node,
  body: PropTypes.oneOfType([PropTypes.element, PropTypes.string]),
  buttonLabel: PropTypes.string,
  onClick: PropTypes.func,
  numbers: PropTypes.array,
  chips: PropTypes.array,
  disabled: PropTypes.bool,
  padding: PropTypes.bool,
  paddingLogo: PropTypes.bool,
  footer: PropTypes.object,
  buttonIcon: PropTypes.element,
};

export default SFThumbnail;
